import { useState, ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Sword,
  ScrollText,
  Sparkles,
  Settings,
  LogIn,
  LogOut,
  Menu,
  X,
  ChevronLeft,
  Music,
  Skull,
  DoorOpen,
} from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';

interface NavItem {
  path: string;
  label: string;
  icon: ReactNode;
  requiresAuth?: boolean;
}

const navItems: NavItem[] = [
  { path: '/tracker', label: 'Items', icon: <Sword size={20} /> },
  { path: '/tracker/notes', label: 'Hints & Notes', icon: <ScrollText size={20} /> },
  { path: '/tracker/songs', label: 'Songs', icon: <Music size={20} /> },
  { path: '/tracker/enemies', label: 'Enemies', icon: <Skull size={20} /> },
  { path: '/tracker/entrances', label: 'Entrances', icon: <DoorOpen size={20} /> },
  { path: '/tricks', label: 'Tricks', icon: <Sparkles size={20} /> },
  { path: '/tracker/settings', label: 'Settings', icon: <Settings size={20} /> },
];

export function AppShell({ children }: { children: ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  return (
    <div className="flex h-screen bg-gray-950 text-gray-100">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0 z-50
          flex flex-col bg-gray-900 border-r border-gray-800
          transition-all duration-200
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${sidebarOpen ? 'w-56' : 'w-16'}
        `}
      >
        {/* Logo */}
        <div className="flex items-center h-14 px-4 border-b border-gray-800">
          <img src="/triforce.png" alt="Triforce" className="w-8 h-8 flex-shrink-0" />
          {sidebarOpen && (
            <span className="ml-3 font-bold text-zelda-gold truncate">
              Zelda Tracker
            </span>
          )}
          <button
            onClick={() => setMobileOpen(false)}
            className="ml-auto lg:hidden text-gray-400 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg
                  transition-colors duration-150
                  ${isActive
                    ? 'bg-zelda-gold/20 text-zelda-gold'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }
                `}
                title={!sidebarOpen ? item.label : undefined}
              >
                {item.icon}
                {sidebarOpen && <span className="text-sm font-medium">{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-800 p-4 space-y-2">
          {isAuthenticated ? (
            <>
              {sidebarOpen && (
                <div className="text-xs text-gray-500 truncate">{user?.username}</div>
              )}
              <button
                onClick={logout}
                className="flex items-center gap-3 text-sm text-gray-400 hover:text-red-400 transition-colors w-full"
                title="Logout"
              >
                <LogOut size={18} />
                {sidebarOpen && <span>Logout</span>}
              </button>
            </>
          ) : (
            <Link
              to="/login"
              className="flex items-center gap-3 text-sm text-gray-400 hover:text-zelda-gold transition-colors"
            >
              <LogIn size={18} />
              {sidebarOpen && <span>Login</span>}
            </Link>
          )}

          {/* Collapse toggle (desktop) */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:flex items-center gap-3 text-sm text-gray-500 hover:text-gray-300 transition-colors w-full"
          >
            <ChevronLeft size={18} className={`transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
            {sidebarOpen && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex items-center h-14 px-4 border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm lg:hidden">
          <button
            onClick={() => setMobileOpen(true)}
            className="text-gray-400 hover:text-white"
          >
            <Menu size={24} />
          </button>
          <img src="/triforce.png" alt="" className="w-6 h-6 ml-3" />
          <span className="ml-2 font-bold text-zelda-gold">Zelda Tracker</span>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
